import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../utils/cn';
import { StatusBadge } from '../../../../components/ui/StatusBadge';

interface RatingScaleProps {
  value: number | null;
  onChange: (rating: number) => void;
  question: string;
}

const RatingScale: React.FC<RatingScaleProps> = ({ value, onChange, question }) => {
  const { t } = useTranslation();
  const ratings = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  const getRatingLabel = (rating: number) => {
    if (rating <= 6) return t('performance.espn.detractor');
    if (rating <= 8) return t('performance.espn.passive');
    return t('performance.espn.promoter');
  };

  const getRatingVariant = (rating: number): 'error' | 'warning' | 'success' => {
    if (rating <= 6) return 'error';
    if (rating <= 8) return 'warning';
    return 'success';
  };

  const getButtonColors = (rating: number, isSelected: boolean) => {
    if (!isSelected) return 'border-border text-foreground hover:bg-muted';
    if (rating <= 6) return 'bg-destructive text-destructive-foreground border-destructive';
    if (rating <= 8) return 'bg-amber-500 text-white border-amber-500';
    return 'bg-emerald-500 text-white border-emerald-500';
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">{question}</h3>

      <div className="flex justify-between items-center mb-2">
        <span className="text-xs text-muted-foreground">{t('performance.espn.notLikely')}</span>
        <span className="text-xs text-muted-foreground">{t('performance.espn.extremelyLikely')}</span>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {ratings.map((rating) => (
          <button
            key={rating}
            type="button"
            onClick={() => onChange(rating)}
            className={cn(
              'min-w-[44px] h-12 flex-1 rounded-md border font-semibold text-sm transition-all duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              getButtonColors(rating, value === rating),
              value === rating && 'motion-safe:scale-110'
            )}
          >
            {rating}
          </button>
        ))}
      </div>

      {value !== null && (
        <div className="text-center mt-4">
          <StatusBadge variant={getRatingVariant(value)}>
            {getRatingLabel(value)}
          </StatusBadge>
        </div>
      )}
    </div>
  );
};

export default RatingScale;
